import React, { useState, useEffect, useCallback } from 'react';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Print as PrintIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useNavigate } from 'react-router-dom';
import {
  eventFormsApi,
  EventForm,
  PaginatedEventFormsResponse,
} from '../services/eventFormsService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';
import { fabricJsonToDataUrl } from '../components/TableSetUpCanvas.tsx';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const EventFormsListPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const { showError, showSuccess } = useSnackbar();
  const navigate = useNavigate();
  const [eventForms, setEventForms] = useState<EventForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EventForm | null>(null);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.type === 'admin';

  const fetchEventForms = useCallback(async (pageNum: number = 1) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedEventFormsResponse =
        await eventFormsApi.getEventForms(
          { 
            page: pageNum, 
            limit,
            ...(isHistoryView ? { type: 'history' as const } : {})
          },
          sessionToken
        );

      setEventForms(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch event forms';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, limit, isHistoryView, showError]);

  useEffect(() => {
    fetchEventForms();
  }, [fetchEventForms]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    fetchEventForms(value);
  };

  const handleHistoryToggle = () => {
    setIsHistoryView(!isHistoryView);
    setPage(1); // Reset to first page when switching views
  };

  const handleEdit = (id: number) => {
    navigate(`/event-form?id=${id}`);
  };

  const handleCreateNew = () => {
    navigate('/event-form');
  };

  const handleCopy = (form: EventForm) => {
    navigate('/event-form', { state: { copyFrom: form } });
  };

  const handleDeleteClick = (form: EventForm) => {
    setDeleteTarget(form);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !sessionToken) return;

    setDeleting(true);
    try {
      await eventFormsApi.deleteEventForm(deleteTarget.id, sessionToken);
      showSuccess('Event form deleted successfully!');
      setDeleteTarget(null);
      fetchEventForms(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event form';
      showError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = async (id: number) => {
    if (!sessionToken) return;

    try {
      const form = await eventFormsApi.getEventForm(id, sessionToken);
      
      // Parse JSON fields
      let serverIds: number[] = [];
      let menuOptions: string[] = [];
      
      if (form.servers) {
        try {
          serverIds = JSON.parse(form.servers);
        } catch (e) {
          console.error('Error parsing servers:', e);
        }
      }
      
      if (form.menu) {
        try {
          menuOptions = JSON.parse(form.menu);
        } catch (e) {
          console.error('Error parsing menu:', e);
        }
      }

      // Fetch employees to get names
      let employees: Employee[] = [];
      if (serverIds.length > 0 && sessionToken) {
        try {
          const employeesResponse = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
          employees = employeesResponse.data;
        } catch (e) {
          console.error('Error fetching employees:', e);
        }
      }

      // Render Fabric canvas (table set up) to image for print
      let tableSetUpImageDataUrl: string | null = null;
      if (form.tableSetUp && form.tableSetUp.trim()) {
        tableSetUpImageDataUrl = await fabricJsonToDataUrl(form.tableSetUp);
      }

      // Generate print HTML
      const html = generateEventFormPrintHTML(form, serverIds, menuOptions, employees, tableSetUpImageDataUrl);
      
      // Open print dialog
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to print event form';
      showError(errorMessage);
    }
  };

  const generateEventFormPrintHTML = (form: EventForm, serverIds: number[], menuOptions: string[], employees: Employee[], tableSetUpImageDataUrl: string | null = null): string => {
    const formatDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch {
        return dateString;
      }
    };

    // Get employee names for servers
    const serverNames = serverIds
      .map(id => {
        const emp = employees.find(e => e.id === id);
        return emp ? emp.name : `Employee #${id}`;
      })
      .join(', ');
    const serversDisplay = serverIds.length > 0 ? serverNames : '-';

    const cell = (value: string | number | null | undefined) => {
      const isEmpty = value === null || value === undefined || value === '';
      return isEmpty ? '<span class="empty">Not specified</span>' : String(value).replace(/\n/g, '<br>');
    };

    const field = (label: string, value: string | number | null | undefined, colspan: number) =>
      `<td class="field" colspan="${colspan}"><div class="flabel">${label}</div><div class="fval">${cell(value)}</div></td>`;

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Event Form - ${formatDate(form.eventDate)}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: letter;
            margin: 0.35in;
          }

          body {
            font-family: Arial, sans-serif;
            color: #222;
            background: white;
          }

          .header {
            text-align: center;
            margin-bottom: 8px;
            border-bottom: 2px solid #1976d2;
            padding-bottom: 6px;
            flex-shrink: 0;
          }

          .header h1 {
            color: #850d00;
            font-size: 18px;
            font-weight: bold;
          }

          .header .form-title {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 2px;
          }

          .header .address {
            font-size: 9.5px;
            color: #666;
            margin-top: 2px;
          }

          .form-page {
            height: 10.3in;
            padding: 0.15in;
            page-break-after: always;
            display: flex;
            flex-direction: column;
          }

          .table-wrap {
            flex: 1;
            display: flex;
          }

          .form-table {
            width: 100%;
            height: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          .form-table td,
          .form-table th {
            border: 1px solid #333;
            vertical-align: top;
            word-wrap: break-word;
          }

          .form-table th.section-th {
            background-color: #e0e0e0;
            color: #850d00;
            font-weight: bold;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            text-align: center;
            padding: 3px 6px;
          }

          .field {
            padding: 4px 8px;
          }

          .flabel {
            font-size: 9px;
            font-weight: bold;
            text-transform: uppercase;
            color: #850d00;
            letter-spacing: 0.2px;
            line-height: 1.1;
          }

          .fval {
            font-size: 12px;
            line-height: 1.3;
            margin-top: 2px;
            min-height: 14px;
            color: #000;
          }

          .fval .empty {
            color: #999;
            font-style: italic;
          }

          .menu-cell {
            padding: 4px 8px;
          }

          .menu-cell ul {
            list-style: disc;
            margin: 2px 0 0 16px;
            font-size: 12px;
            line-height: 1.3;
            color: #000;
          }

          .table-set-up-page h3 {
            color: #1976d2;
            font-weight: bold;
            font-size: 16px;
            text-align: center;
            margin-bottom: 12px;
          }

          .diagram-container {
            position: relative;
            height: 800px;
            width: 100%;
            max-width: 800px;
            border: 3px solid #000;
            box-sizing: border-box;
            font-family: 'Segoe UI', Arial, sans-serif;
            margin: 20px auto;
            background-color: #fff;
            overflow: hidden;
          }

          @media print {
            .form-page {
              page-break-after: always;
            }
          }
        </style>
      </head>
      <body>
        <div class="form-page">
          <div class="header">
            <h1>Post Office Pub</h1>
            <div class="form-title">Event Form${form.name ? ` &mdash; ${form.name}` : ''}</div>
            <div class="address">1 Ray St, North Grafton, MA 01536, United States. Phone: +1 508-839-6106</div>
          </div>

          <div class="table-wrap">
          <table class="form-table">
            <colgroup>
              ${Array.from({ length: 12 }).map(() => '<col style="width:8.3333%">').join('')}
            </colgroup>
            <tbody>
              <tr>
                ${field('Status', form.status, 6)}
                ${field('Event Date', formatDate(form.eventDate), 6)}
              </tr>

              <tr>
                ${field('Set up/Decorate', form.setUpDecorate, 4)}
                ${field('Guest Arrival', form.guestArrival, 4)}
                ${field('Food', form.food, 4)}
              </tr>

              <tr>
                ${field('Server(s)', serverIds.length > 0 ? serversDisplay : null, 12)}
              </tr>

              <tr><th colspan="12" class="section-th">Event Details</th></tr>
              <tr>
                ${field('Name', form.name, 6)}
                ${field('Who is Event for', form.eventFor, 6)}
              </tr>
              <tr>
                ${field('Phone Number', formatPhoneNumber(form.phone), 6)}
                ${field('Email', form.email, 6)}
              </tr>
              <tr>
                ${field('Deposit Date and Manager', form.depositDateManager, 12)}
              </tr>

              <tr>
                ${field('Time', form.time, 2)}
                ${field('Room', form.room, 2)}
                ${field('Event', form.event, 2)}
                ${field('Exp Guest', form.expGuest, 3)}
                ${field('Final Count', form.finalCount, 3)}
              </tr>

              <tr><th colspan="12" class="section-th">Menu Options</th></tr>
              <tr>
                <td colspan="12" class="menu-cell">${menuOptions.length > 0
                  ? `<ul>${menuOptions.map(option => `<li>${option}</li>`).join('')}</ul>`
                  : '<span class="empty">No menu options specified</span>'}</td>
              </tr>

              <tr>
                ${field('Chalkboard Message', form.chalkboardMessage, 12)}
              </tr>
              <tr>
                ${field('Beverages (Open bar/pitchers, checks)', form.beverages, 12)}
              </tr>
              <tr>
                ${field('Tables (Gift, Cake, check-in)', form.tables, 12)}
              </tr>
              <tr>
                ${field('Linen(s)', form.linen, 12)}
              </tr>
              <tr>
                ${field('Misc', form.misc, 12)}
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <div class="table-set-up-page">
          <h3>Table Set Up</h3>
          <div class="diagram-container">
            ${tableSetUpImageDataUrl
              ? `<img src='${tableSetUpImageDataUrl}' alt="Table Set Up" style="max-width: 100%; height: auto; display: block;" />`
              : '<p style="color:#999; font-style: italic; padding: 20px;">No table set up diagram</p>'}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && eventForms.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Event Forms
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={isHistoryView ? "contained" : "outlined"}
            onClick={handleHistoryToggle}
          >
            History
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchEventForms(page)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Event Date', 'Time', 'Name', 'Email', 'Phone', 'Status', 'Event Name', 'No. of Guests', 'Actions'].map((h, i) => (
                  <TableCell key={h} align={h === 'Actions' ? 'center' : 'left'} sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.04em',
                    textTransform: 'uppercase', border: '2px solid #7f0000', py: 1.5, px: 1.5, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {eventForms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                    No event forms found
                  </TableCell>
                </TableRow>
              ) : (
                eventForms.map((form, idx) => (
                  <TableRow key={form.id} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5', '&:hover': { backgroundColor: '#fce4e4' } }}>
                    <TableCell>{formatDate(form.eventDate)}</TableCell>
                    <TableCell>{form.time || '—'}</TableCell>
                    <TableCell>{form.name || '—'}</TableCell>
                    <TableCell>{form.email || '—'}</TableCell>
                    <TableCell>{formatPhoneNumber(form.phone) || '—'}</TableCell>
                    <TableCell>
                      {form.status != null && form.status.trim() !== '' ? (
                        <Chip label={form.status} color={getStatusColor(form.status) as any} size="small" sx={{ borderRadius: 1 }} />
                      ) : ''}
                    </TableCell>
                    <TableCell>{form.event || '—'}</TableCell>
                    <TableCell>{form.expGuest !== null && form.expGuest !== undefined ? form.expGuest : '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton color="secondary" onClick={() => handleEdit(form.id)} aria-label="edit" size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="default" onClick={() => handlePrint(form.id)} aria-label="print" size="small">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="info" onClick={() => handleCopy(form)} aria-label="copy" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      {isAdmin && (
                        <IconButton color="error" onClick={() => handleDeleteClick(form)} aria-label="delete" size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {eventForms.length} of {total} event form
              {total !== 1 ? 's' : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Event Form</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the event form
            {deleteTarget?.name ? ` for "${deleteTarget.name}"` : ''}
            {deleteTarget ? ` on ${formatDate(deleteTarget.eventDate)}` : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventFormsListPage;

